import type {
  Fencer,
  AttendanceRecord,
  Statistics,
  DateRange,
  CompetitionResult,
  Competition,
  CompetitionParticipation,
  AgeCategory,
} from "../types"

// API Interface - can be swapped for real API later
export interface FencerAPI {
  // Fencer management
  getFencers(): Promise<Fencer[]>
  addFencer(fencer: Omit<Fencer, "id">): Promise<Fencer>
  updateFencer(id: string, fencer: Partial<Fencer>): Promise<Fencer>
  deleteFencer(id: string): Promise<void>

  // Attendance management
  getAttendanceRecords(dateRange?: DateRange): Promise<AttendanceRecord[]>
  addAttendanceRecord(record: Omit<AttendanceRecord, "id">): Promise<AttendanceRecord>
  updateAttendanceRecord(id: string, record: Partial<AttendanceRecord>): Promise<AttendanceRecord>

  // Competition management
  getCompetitions(): Promise<Competition[]>
  getCompetition(id: string): Promise<Competition | null>
  addCompetition(competition: Omit<Competition, "id">): Promise<Competition>
  updateCompetition(id: string, competition: Partial<Competition>): Promise<Competition>
  deleteCompetition(id: string): Promise<void>

  // Competition participation
  getCompetitionParticipations(competitionId?: string, fencerId?: string): Promise<CompetitionParticipation[]>
  addCompetitionParticipation(participation: Omit<CompetitionParticipation, "id">): Promise<CompetitionParticipation>
  updateCompetitionParticipation(
    id: string,
    participation: Partial<CompetitionParticipation>,
  ): Promise<CompetitionParticipation>
  deleteCompetitionParticipation(id: string): Promise<void>

  // Competition results
  getCompetitionResults(competitionId?: string, fencerId?: string, dateRange?: DateRange): Promise<CompetitionResult[]>
  addCompetitionResult(result: Omit<CompetitionResult, "id">): Promise<CompetitionResult>
  updateCompetitionResult(id: string, result: Partial<CompetitionResult>): Promise<CompetitionResult>
  deleteCompetitionResult(id: string): Promise<void>

  // Statistics
  getStatistics(dateRange: DateRange): Promise<Statistics>
}

// Old format interfaces for migration
interface OldAttendanceRecord {
  id: string
  fencerId: string
  date: string
  present: boolean
  type: "training" | "private-lesson"
  notes?: string
}

interface OldCompetitionResult {
  id: string
  fencerId: string
  competitionName: string
  date: string
  ageCategory: string
  placement: number
  totalParticipants: number
  notes?: string
}

interface OldFencer {
  id: string
  name: string
  email: string
  phone: string
  dateOfBirth: string
  parentName?: string
  parentPhone?: string
  registrationDate: string
}

// LocalStorage implementation
class LocalStorageAPI implements FencerAPI {
  private generateId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9)
  }

  async getFencers(): Promise<Fencer[]> {
    const fencers = localStorage.getItem("fencers")

    // Handle migration from old format to new format with guardians array
    if (fencers) {
      const parsedFencers = JSON.parse(fencers) as (Fencer | OldFencer)[]

      // Check if we need to migrate
      if (parsedFencers.length > 0 && !("guardians" in parsedFencers[0])) {
        const migratedFencers = parsedFencers.map((fencer) => {
          const oldFencer = fencer as OldFencer
          const guardians = []

          // If there was parent info, convert it to a guardian
          if (oldFencer.parentName) {
            guardians.push({
              id: this.generateId(),
              name: oldFencer.parentName,
              phone: oldFencer.parentPhone || "",
              relationship: "Parent/Guardian",
            })
          }

          // Create new fencer object without the old parent fields
          const newFencer: Fencer = {
            id: oldFencer.id,
            name: oldFencer.name,
            email: oldFencer.email,
            phone: oldFencer.phone,
            dateOfBirth: oldFencer.dateOfBirth,
            registrationDate: oldFencer.registrationDate,
            guardians,
          }

          return newFencer
        })

        // Save the migrated data
        localStorage.setItem("fencers", JSON.stringify(migratedFencers))
        return migratedFencers
      }

      return parsedFencers as Fencer[]
    }

    return []
  }

  async addFencer(fencer: Omit<Fencer, "id">): Promise<Fencer> {
    const fencers = await this.getFencers()
    const newFencer: Fencer = {
      ...fencer,
      id: this.generateId(),
    }
    fencers.push(newFencer)
    localStorage.setItem("fencers", JSON.stringify(fencers))
    return newFencer
  }

  async updateFencer(id: string, fencer: Partial<Fencer>): Promise<Fencer> {
    const fencers = await this.getFencers()
    const index = fencers.findIndex((f) => f.id === id)
    if (index === -1) throw new Error("Fencer not found")

    fencers[index] = { ...fencers[index], ...fencer }
    localStorage.setItem("fencers", JSON.stringify(fencers))
    return fencers[index]
  }

  async deleteFencer(id: string): Promise<void> {
    const fencers = await this.getFencers()
    const filtered = fencers.filter((f) => f.id !== id)
    localStorage.setItem("fencers", JSON.stringify(filtered))
  }

  async getAttendanceRecords(dateRange?: DateRange): Promise<AttendanceRecord[]> {
    const records = localStorage.getItem("attendanceRecords")
    let allRecords: AttendanceRecord[] = records ? JSON.parse(records) : []

    // Handle migration from old format to new format
    if (allRecords.length > 0 && "type" in allRecords[0]) {
      const oldRecords = allRecords as unknown as OldAttendanceRecord[]
      const migratedRecords = []
      const recordsByFencerAndDate = new Map()

      // Group records by fencer and date
      for (const record of oldRecords) {
        const key = `${record.fencerId}-${record.date}`
        if (!recordsByFencerAndDate.has(key)) {
          recordsByFencerAndDate.set(key, { training: null, privateLesson: null })
        }

        const entry = recordsByFencerAndDate.get(key)
        if (record.type === "training") {
          entry.training = record
        } else if (record.type === "private-lesson") {
          entry.privateLesson = record
        }
      }

      // Create new format records
      for (const [key, entry] of recordsByFencerAndDate.entries()) {
        const [fencerId, date] = key.split("-")
        const present = entry.training?.present || false
        const privateLessonReceived = entry.privateLesson?.present || false

        migratedRecords.push({
          id: this.generateId(),
          fencerId,
          date,
          present,
          privateLessonReceived,
          notes: entry.training?.notes || "",
        })
      }

      // Save the migrated data
      localStorage.setItem("attendanceRecords", JSON.stringify(migratedRecords))
      allRecords = migratedRecords
    }

    if (dateRange) {
      allRecords = allRecords.filter((record) => record.date >= dateRange.start && record.date <= dateRange.end)
    }

    return allRecords
  }

  async addAttendanceRecord(record: Omit<AttendanceRecord, "id">): Promise<AttendanceRecord> {
    const records = await this.getAttendanceRecords()
    const newRecord: AttendanceRecord = {
      ...record,
      id: this.generateId(),
    }
    records.push(newRecord)
    localStorage.setItem("attendanceRecords", JSON.stringify(records))
    return newRecord
  }

  async updateAttendanceRecord(id: string, record: Partial<AttendanceRecord>): Promise<AttendanceRecord> {
    const records = await this.getAttendanceRecords()
    const index = records.findIndex((r) => r.id === id)
    if (index === -1) throw new Error("Record not found")

    records[index] = { ...records[index], ...record }
    localStorage.setItem("attendanceRecords", JSON.stringify(records))
    return records[index]
  }

  async getCompetitions(): Promise<Competition[]> {
    const competitions = localStorage.getItem("competitions")
    return competitions ? JSON.parse(competitions) : []
  }

  async getCompetition(id: string): Promise<Competition | null> {
    const competitions = await this.getCompetitions()
    return competitions.find((c) => c.id === id) || null
  }

  async addCompetition(competition: Omit<Competition, "id">): Promise<Competition> {
    const competitions = await this.getCompetitions()
    const newCompetition: Competition = {
      ...competition,
      id: this.generateId(),
    }
    competitions.push(newCompetition)
    localStorage.setItem("competitions", JSON.stringify(competitions))
    return newCompetition
  }

  async updateCompetition(id: string, competition: Partial<Competition>): Promise<Competition> {
    const competitions = await this.getCompetitions()
    const index = competitions.findIndex((c) => c.id === id)
    if (index === -1) throw new Error("Competition not found")

    competitions[index] = { ...competitions[index], ...competition }
    localStorage.setItem("competitions", JSON.stringify(competitions))
    return competitions[index]
  }

  async deleteCompetition(id: string): Promise<void> {
    const competitions = await this.getCompetitions()
    const filtered = competitions.filter((c) => c.id !== id)
    localStorage.setItem("competitions", JSON.stringify(filtered))

    // Also delete related participation records and results
    const participations = await this.getCompetitionParticipations(id)
    for (const participation of participations) {
      await this.deleteCompetitionParticipation(participation.id)
    }

    const results = await this.getCompetitionResults(id)
    for (const result of results) {
      await this.deleteCompetitionResult(result.id)
    }
  }

  async getCompetitionParticipations(competitionId?: string, fencerId?: string): Promise<CompetitionParticipation[]> {
    const participations = localStorage.getItem("competitionParticipations")
    let allParticipations: CompetitionParticipation[] = participations ? JSON.parse(participations) : []

    if (competitionId) {
      allParticipations = allParticipations.filter((p) => p.competitionId === competitionId)
    }

    if (fencerId) {
      allParticipations = allParticipations.filter((p) => p.fencerId === fencerId)
    }

    return allParticipations
  }

  async addCompetitionParticipation(
    participation: Omit<CompetitionParticipation, "id">,
  ): Promise<CompetitionParticipation> {
    const participations = await this.getCompetitionParticipations()
    const newParticipation: CompetitionParticipation = {
      ...participation,
      id: this.generateId(),
    }
    participations.push(newParticipation)
    localStorage.setItem("competitionParticipations", JSON.stringify(participations))
    return newParticipation
  }

  async updateCompetitionParticipation(
    id: string,
    participation: Partial<CompetitionParticipation>,
  ): Promise<CompetitionParticipation> {
    const participations = await this.getCompetitionParticipations()
    const index = participations.findIndex((p) => p.id === id)
    if (index === -1) throw new Error("Participation record not found")

    participations[index] = { ...participations[index], ...participation }
    localStorage.setItem("competitionParticipations", JSON.stringify(participations))
    return participations[index]
  }

  async deleteCompetitionParticipation(id: string): Promise<void> {
    const participations = await this.getCompetitionParticipations()
    const filtered = participations.filter((p) => p.id !== id)
    localStorage.setItem("competitionParticipations", JSON.stringify(filtered))
  }

  async getCompetitionResults(
    competitionId?: string,
    fencerId?: string,
    dateRange?: DateRange,
  ): Promise<CompetitionResult[]> {
    const results = localStorage.getItem("competitionResults")
    let allResults: CompetitionResult[] = results ? JSON.parse(results) : []

    // Handle migration from old format to new format
    if (allResults.length > 0 && !("competitionId" in allResults[0])) {
      const oldResults = allResults as unknown as OldCompetitionResult[]
      // Get competitions to see if we need to create them
      const competitions = await this.getCompetitions()
      const competitionMap = new Map()

      // Create a map of existing competitions by name and date
      for (const comp of competitions) {
        competitionMap.set(`${comp.name}-${comp.date}`, comp.id)
      }

      // Migrate results
      const migratedResults = []
      for (const result of oldResults) {
        // Check if we have a competition for this result
        let competitionId
        const key = `${result.competitionName}-${result.date}`

        if (competitionMap.has(key)) {
          competitionId = competitionMap.get(key)
        } else {
          // Create a new competition
          const newCompetition = await this.addCompetition({
            name: result.competitionName,
            date: result.date,
            location: "Unknown",
            ageCategories: [result.ageCategory as any],
            notes: "",
          })
          competitionId = newCompetition.id
          competitionMap.set(key, competitionId)
        }

        // Create new format result
        migratedResults.push({
          id: result.id,
          competitionId,
          fencerId: result.fencerId,
          ageCategory: result.ageCategory as AgeCategory,
          placement: result.placement,
          totalParticipants: result.totalParticipants,
          notes: result.notes || "",
        })
      }

      // Save the migrated data
      localStorage.setItem("competitionResults", JSON.stringify(migratedResults))
      allResults = migratedResults
    }

    if (competitionId) {
      allResults = allResults.filter((r) => r.competitionId === competitionId)
    }

    if (fencerId) {
      allResults = allResults.filter((r) => r.fencerId === fencerId)
    }

    if (dateRange) {
      // For date range, we need to get the competition dates
      const competitions = await this.getCompetitions()
      const competitionDates = new Map()
      for (const comp of competitions) {
        competitionDates.set(comp.id, comp.date)
      }

      allResults = allResults.filter((r) => {
        const date = competitionDates.get(r.competitionId)
        return date && date >= dateRange.start && date <= dateRange.end
      })
    }

    return allResults.sort((a, b) => {
      // We can't use async getCompetition here, so we'll sort by competitionId for now
      return b.competitionId.localeCompare(a.competitionId)
    })
  }

  async addCompetitionResult(result: Omit<CompetitionResult, "id">): Promise<CompetitionResult> {
    const results = await this.getCompetitionResults()
    const newResult: CompetitionResult = {
      ...result,
      id: this.generateId(),
    }
    results.push(newResult)
    localStorage.setItem("competitionResults", JSON.stringify(results))
    return newResult
  }

  async updateCompetitionResult(id: string, result: Partial<CompetitionResult>): Promise<CompetitionResult> {
    const results = await this.getCompetitionResults()
    const index = results.findIndex((r) => r.id === id)
    if (index === -1) throw new Error("Result not found")

    results[index] = { ...results[index], ...result }
    localStorage.setItem("competitionResults", JSON.stringify(results))
    return results[index]
  }

  async deleteCompetitionResult(id: string): Promise<void> {
    const results = await this.getCompetitionResults()
    const filtered = results.filter((r) => r.id !== id)
    localStorage.setItem("competitionResults", JSON.stringify(filtered))
  }

  async getStatistics(dateRange: DateRange): Promise<Statistics> {
    const fencers = await this.getFencers()
    const records = await this.getAttendanceRecords(dateRange)

    const trainingRecords = records.filter((r) => r.present)
    const privateLessonRecords = records.filter((r) => r.present && r.privateLessonReceived)

    // Group by date
    const trainingByDate = trainingRecords.reduce(
      (acc, record) => {
        acc[record.date] = (acc[record.date] || 0) + 1
        return acc
      },
      {} as Record<string, number>,
    )

    const privateLessonsByDate = privateLessonRecords.reduce(
      (acc, record) => {
        acc[record.date] = (acc[record.date] || 0) + 1
        return acc
      },
      {} as Record<string, number>,
    )

    // Fencer statistics
    const fencerStats = fencers.map((fencer) => {
      const trainingCount = trainingRecords.filter((r) => r.fencerId === fencer.id).length
      const privateLessonCount = privateLessonRecords.filter((r) => r.fencerId === fencer.id).length

      return {
        fencerId: fencer.id,
        name: fencer.name,
        trainingCount,
        privateLessonCount,
      }
    })

    return {
      totalFencers: fencers.length,
      trainingAttendance: Object.entries(trainingByDate).map(([date, count]) => ({ date, count })),
      privateLessons: Object.entries(privateLessonsByDate).map(([date, count]) => ({ date, count })),
      fencerStats,
    }
  }
}

// Export the API instance - can be swapped for different implementations
export const api: FencerAPI = new LocalStorageAPI()
